"use client";

/**
 * The shared announcement channel — Phase 02.
 *
 * WHY. `aria-live` appears exactly once in the whole product (audit F52), so no
 * async outcome is announced: an optimistic like that rolls back, a bid that
 * fails, a swap that succeeds — all of them are visible-only. A toast is not a
 * substitute: sonner's viewport announces its own content, but the toast is
 * also the only record (see `DurableResult`), and plenty of outcomes have no
 * toast at all.
 *
 * WHAT THIS IS NOT. It is not a notification system and not a replacement for
 * rendering the outcome. A live region is an *additional* channel for something
 * already on screen, or for something that happened with no visual change (a
 * rollback). Use it alongside the visual result, never instead of it.
 *
 * WHEN TO USE `assertive`. Only when the user must stop what they are doing:
 * a failure, a rollback, a destructive result. Everything else is polite, or it
 * interrupts the user's own typing.
 *
 * Two singleton regions live at the end of `<body>` — one polite, one
 * assertive — created on first use. Two are required because a region's
 * politeness is fixed at the element, and re-using one region for both would
 * make every message as loud as the loudest.
 */

type Politeness = "polite" | "assertive";

const REGION_ID: Record<Politeness, string> = {
  polite: "nb-live-polite",
  assertive: "nb-live-assertive",
};

function region(politeness: Politeness): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const id = REGION_ID[politeness];
  let el = document.getElementById(id);
  if (el) return el;

  el = document.createElement("div");
  el.id = id;
  el.setAttribute("role", politeness === "assertive" ? "alert" : "status");
  el.setAttribute("aria-live", politeness);
  // `all` rather than the default `additions text`: a message replacing a
  // previous one of the same length was not being re-announced.
  el.setAttribute("aria-relevant", "all");
  el.setAttribute("aria-atomic", "true");
  // Visually hidden without being hidden from assistive technology.
  // `display:none` / `visibility:hidden` would remove it from the a11y tree,
  // and `width/height: 0` stops some screen readers from reading it at all.
  el.style.cssText =
    "position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;";
  document.body.appendChild(el);
  return el;
}

/**
 * Announce a message to assistive technology.
 *
 * The text is cleared first and set on the next frame: setting the same string
 * twice in a row is a no-op to the accessibility tree, so a repeated failure
 * ("still offline") would be announced once and then never again.
 */
export function announce(message: string, politeness: Politeness = "polite") {
  const el = region(politeness);
  if (!el || !message) return;
  el.textContent = "";
  requestAnimationFrame(() => {
    el.textContent = message;
  });
}
